import { InjectProperty } from '@jack-henry/frontend-utils/di/index.js';
import { AchExceptionsService } from '@treasury/domain/channel/services/positive-pay/ach-exceptions-service';
import { CheckExceptionsService } from '@treasury/domain/channel/services/positive-pay/check-exceptions-service';
import { AchExceptionDetail } from '@treasury/domain/channel/services/positive-pay/entities/ach-exception-detail';
import { CheckExceptionAggregate } from '@treasury/domain/channel/services/positive-pay/entities/check-exception-aggregate';
import { CheckExceptionReturnReason } from '@treasury/domain/channel/services/positive-pay/entities/check-exception-return-reason';
import { CompanyAccount } from '@treasury/domain/channel/services/positive-pay/entities/company-account';
import { PositivePayOption } from '@treasury/domain/channel/services/positive-pay/entities/positive-pay-option';
import { PositivePayService } from '@treasury/domain/channel/services/positive-pay/positive-pay-service';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { ListeningElementMixin } from '@treasury/omega/components';
import '@treasury/omega/components/omega-alert.js';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './partials/ach-exception-widget/ach-exception-widget.js';
import './partials/check-exception-widget/check-exception-widget.js';
import { type AlertOptions, PositivePayOptions } from './positive-pay.types';

const CHECK_EXCEPTIONS = 'checkExceptions';
const ACH_EXCEPTIONS = 'achExceptions';

type CustomError = Error & {
    code: string;
};

@customElement('positive-pay-widget')
export class PositivePayWidget extends ListeningElementMixin(LitElement) {
    @property({ type: String })
    public exceptionType?: string;

    @property({ type: Number })
    public checkExceptionCount: number;

    @property({ type: Number })
    public achExceptionCount: Number;

    @property({ type: Object })
    public checkExceptionRecords?: CheckExceptionAggregate;

    @property({ type: Array })
    public achExceptionRecords?: AchExceptionDetail[];

    @property({ type: Boolean })
    public isReviewStep?: boolean;

    @property({ type: Boolean })
    public loadingCheckExceptions = true;

    @property({ type: Boolean })
    public loadingAchExceptions = true;

    @property({ type: Array })
    public options?: PositivePayOption[];

    @property({ type: Boolean })
    public disableCheckExceptions?: boolean;

    @InjectProperty({
        async: true,
        token: EntitlementsService,
    })
    declare private readonly entitlementsService: Promise<EntitlementsService>;

    @InjectProperty()
    declare private readonly positivePayService: PositivePayService;

    @InjectProperty()
    declare private readonly checkExceptionsService: CheckExceptionsService;

    @InjectProperty()
    declare private readonly achExceptionsService: AchExceptionsService;

    @InjectProperty({
        async: true,
        token: FeatureFlagService,
    })
    declare private featureFlagService: Promise<FeatureFlagService>;

    @state()
    private _hasWorkACHExceptionsEntitlement?: boolean;

    @state()
    private _hasWorkCheckExceptionsEntitlement?: boolean;

    @state()
    private _hasWidgetCreateAchFilterRuleButton?: boolean;

    @state()
    private _hasCreateFilterRuleEntitlement?: boolean;

    @state()
    private arpExceptionAccounts?: CompanyAccount[];

    @state()
    private checkExceptionReturnReasons?: CheckExceptionReturnReason[];

    @state()
    private alert: AlertOptions = {
        title: '',
        message: '',
        visible: false,
        code: '',
    };

    private get workCheckExceptionsOptionEnabled() {
        return this.hasOption(PositivePayOptions.workCheckExceptions);
    }

    private get workAchExceptionsOptionEnabled() {
        return this.hasOption(PositivePayOptions.workAchExceptions);
    }

    constructor() {
        super();
        this.checkExceptionCount = 0;
        this.achExceptionCount = 0;
    }

    protected async firstUpdated() {
        if (this.parentElement) {
            this.parentElement.style.overflow = 'hidden';
        }

        try {
            const ffService = await this.featureFlagService;
            const entitlementsService = await this.entitlementsService;
            this._hasWorkACHExceptionsEntitlement = await entitlementsService.isEnabled(
                Entitlements.WorkAchExceptions
            );
            this._hasWorkCheckExceptionsEntitlement = await entitlementsService.isEnabled(
                Entitlements.WorkCheckExceptions
            );

            this._hasCreateFilterRuleEntitlement = await entitlementsService.isEnabled(
                Entitlements.CreateAchFilter
            );

            this._hasWidgetCreateAchFilterRuleButton = await ffService.isEnabled(
                Feature.WidgetCreateAchFilterRuleButton
            );

            // Load options first to avoid race condition
            await this.getOptions();
            if (this.workAchExceptionsOptionEnabled) {
                this.loadAchExceptions();
                this.exceptionType = ACH_EXCEPTIONS;
            }

            if (this.workCheckExceptionsOptionEnabled) {
                this.loadCheckExceptions();
                // RULE: If both options are enabled then we want check exceptions to be selected by default
                this.exceptionType = CHECK_EXCEPTIONS;
            }
        } catch (error) {
            this.alert = {
                title: '',
                code: '',
                visible: true,
                type: 'error',
                message: 'There was an error loading the positive pay information.',
            };
        }
    }

    /**
     * Checks if the specified option is enabled.
     *
     * @param {string} optionName - The name of the option to check. Use the PositivePayOptions constant for this parameter.
     * @returns {boolean} True if the option is enabled, false otherwise.
     */
    private hasOption(optionName: string) {
        if (!this.options) return false;
        const foundOption = this.options.find(option => option.name === optionName);
        return foundOption && foundOption.value === true;
    }

    private async getOptions() {
        try {
            this.options = await this.positivePayService.getPositivePayOptions();
        } catch (error) {
            this.alert = {
                title: '',
                code: '',
                message: 'No positive pay options found.',
                visible: true,
                type: 'error',
            };
        }
    }

    private async loadCheckExceptions() {
        this.loadingCheckExceptions = true;
        try {
            this.arpExceptionAccounts = await this.positivePayService.getArpExceptionAccounts();
            if (this.arpExceptionAccounts && this.arpExceptionAccounts.length > 0) {
                const params = {
                    parameters: {
                        account: this.arpExceptionAccounts
                            .map((account: CompanyAccount) => account.accountUniqueId)
                            .filter((id): id is string => id !== undefined),
                    },
                };

                this.checkExceptionRecords =
                    await this.checkExceptionsService.searchCheckExceptions(params);
                if (!this.checkExceptionRecords) {
                    throw new Error('No check exceptions found');
                }

                this.checkExceptionCount = this.checkExceptionRecords.totalCount;
                this.checkExceptionReturnReasons =
                    await this.checkExceptionsService.getCheckExceptionReturnReasons();
            } else {
                this.alert = {
                    title: '',
                    code: '',
                    message: 'No ARP exception accounts found.',
                    visible: true,
                    type: 'warning',
                };
                this.loadingCheckExceptions = false;
                this.disableCheckExceptions = true;
                this.setExceptionType(ACH_EXCEPTIONS);
            }
        } catch (error) {
            const apiError = error as CustomError;
            this.alert = {
                title: '',
                code: apiError?.code ?? '',
                message:
                    error instanceof Error
                        ? error.message
                        : 'An error occurred retrieving check exceptions.',
                visible: true,
                type: 'error',
            };
            throw error;
        } finally {
            this.loadingCheckExceptions = false;
        }
    }

    private async loadAchExceptions() {
        this.loadingAchExceptions = true;
        try {
            this.achExceptionRecords = await this.achExceptionsService.getAchExceptionsForWidget();
            this.achExceptionCount = this.achExceptionRecords?.length
                ? this.achExceptionRecords.length
                : 0;
        } catch (error) {
            const apiError = error as CustomError;
            this.alert = {
                title: '',
                code: apiError?.code ?? '',
                message:
                    error instanceof Error
                        ? error.message
                        : 'An error occurred retrieving ACH exceptions.',
                visible: true,
                type: 'error',
            };
            throw error;
        } finally {
            this.loadingAchExceptions = false;
        }
    }

    private setExceptionType(exceptionType: string) {
        // Hide any existing alert when changing exception type
        this.alert = {
            ...this.alert,
            visible: false,
        };
        this.exceptionType = exceptionType;
    }

    private setReviewstep(detail: boolean) {
        this.isReviewStep = detail;
    }

    private renderCheckExceptionButton() {
        if (
            !this._hasWorkCheckExceptionsEntitlement ||
            this.disableCheckExceptions ||
            !this.workCheckExceptionsOptionEnabled
        )
            return nothing;
        return html`
            <omega-button
                type="link"
                ?disabled=${this.exceptionType === CHECK_EXCEPTIONS || this.isReviewStep}
                @click=${() => this.setExceptionType(CHECK_EXCEPTIONS)}
            >
                Check Exceptions
                (${this.loadingCheckExceptions
                    ? html`<omega-progress class="smallest-loader"></omega-progress>`
                    : this.checkExceptionCount})
            </omega-button>
        `;
    }

    private renderAchExceptionButton() {
        if (!this._hasWorkACHExceptionsEntitlement || !this.workAchExceptionsOptionEnabled)
            return nothing;

        return html`
            <omega-button
                type="link"
                ?disabled=${this.exceptionType === ACH_EXCEPTIONS || this.isReviewStep}
                @click=${() => this.setExceptionType(ACH_EXCEPTIONS)}
            >
                ACH Exceptions
                (${this.loadingAchExceptions
                    ? html`<omega-progress class="smallest-loader"></omega-progress>`
                    : this.achExceptionCount})
            </omega-button>
        `;
    }

    private renderHeader() {
        return html`
            <div class="header">
                <div class="menu-wrapper">
                    <span class="widget-title"> Positive Pay </span>
                    <span class="sublinks">
                        ${this.renderCheckExceptionButton()} ${this.renderAchExceptionButton()}
                    </span>
                </div>
                <div class="alert-wrapper">${this.renderAlert()}</div>
            </div>
        `;
    }

    private renderSuccessMessage() {
        this.alert = {
            ...this.alert,
            visible: true,
            message: 'Decisions saved successfully!',
            type: 'success',
        };
    }

    private handleAlert(alert: AlertOptions) {
        this.alert = alert;
    }

    private renderCheckExceptions() {
        if (this.loadingCheckExceptions)
            return html`
                <div class="loader-wrapper">
                    <omega-progress class="bigger-loader"></omega-progress>
                </div>
            `;
        if (this.checkExceptionCount === 0) {
            return html`
                <div class="message-container">
                    <p class="message">There are no check exceptions to review.</p>
                </div>
            `;
        }
        return html` <check-exception-widget
            .records=${this.checkExceptionRecords}
            .returnReasons=${this.checkExceptionReturnReasons}
            .options=${this.options}
            @setReviewStep=${(event: CustomEvent<boolean>) => this.setReviewstep(event.detail)}
            @recordUpdateRequest=${() => this.loadCheckExceptions()}
            @alert=${(e: CustomEvent<AlertOptions>) => this.handleAlert(e.detail)}
        ></check-exception-widget>`;
    }

    private handleSaveAchExceptions() {
        this.loadAchExceptions();
    }

    private renderAchExceptions() {
        if (this.loadingAchExceptions)
            return html`
                <div class="loader-wrapper">
                    <omega-progress class="bigger-loader"></omega-progress>
                </div>
            `;
        if (this.achExceptionCount === 0) {
            return html`
                <div class="message-container">
                    <p class="message">There are no ACH exceptions to review.</p>
                </div>
            `;
        }
        return html`
            <ach-exception-widget
                .records=${this.achExceptionRecords}
                .showFilterRuleButton=${this._hasWidgetCreateAchFilterRuleButton}
                .hasCreateFilterRule=${this._hasCreateFilterRuleEntitlement}
                @setReviewStep=${(event: CustomEvent<boolean>) => this.setReviewstep(event.detail)}
                @saved=${() => this.handleSaveAchExceptions()}
                @showSuccessMessage=${() => this.renderSuccessMessage()}
            ></ach-exception-widget>
        `;
    }

    private renderAlert() {
        const { code, time, message, type, title, actions, posture, visible } = this.alert;
        const renderedCode = code ? html`${code}: ` : nothing;
        const renderedTime = time ? html`<br />Time: ${time}` : nothing;

        return html`
            <div style="padding: 0px 8px;">
                <omega-alert
                    type=${type}
                    title=${title}
                    posture=${posture}
                    ?isVisible=${visible}
                    @close=${() => {
                        this.alert = { ...this.alert, visible: false };
                    }}
                >
                    ${renderedCode} ${message} ${renderedTime} ${actions}
                </omega-alert>
            </div>
        `;
    }

    public render() {
        return html`
            ${this.renderHeader()}
            ${this.exceptionType === CHECK_EXCEPTIONS
                ? this.renderCheckExceptions()
                : this.renderAchExceptions()}
        `;
    }

    public static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
                overflow: hidden;
                height: 100%;
                background-color: white;
                --omega-table-overflow: auto;
                --omega-table-min-height: 280px;
            }
            .smallest-loader {
                width: 5px;
                height: 5px;
            }
            .header {
                display: flex;
                flex: 0 0 54px;
                border-bottom: 1px solid var(--omega-light-grey);
                align-items: center;
            }
            .menu-wrapper {
                display: flex;
                width: 100%;
                align-items: center;
            }
            .alert-wrapper {
                display: flex;
                width: 100%;
                justify-content: flex-end;
            }
            .widget-title {
                font-size: 18px;
                margin-left: 15px;
            }
            .sublinks omega-button {
                text-decoration: none;
                border-left: 1px solid var(--omega-light-grey);
                margin: 0;
            }
            .sublinks omega-button:first-of-type {
                border: none;
            }
            .loader-wrapper {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 85%;
            }
            .bigger-loader {
                width: 50px;
                height: 50px;
                border-width: 6px;
            }
            .message-container {
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .message-container .message {
                font-size: 15px;
            }
        `;
    }
}
